import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Gauge, Sparkles } from 'lucide-react';
import { useState } from 'react';

import {
  mediaOptimization,
  mediaOptimize,
  mediaSetSettings,
  mediaSpace,
} from '@/shared/api/generated/media/media';
import type { SettingsBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Segmented } from '@/shared/ui/Segmented';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Slider } from '@/shared/ui/Slider';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import { humanSize } from './format';
import styles from './OptimizationDialog.module.scss';
import type { Environment } from './useUploader';

const SIDES = [1920, 2560, 3840];

/**
 * Оптимизация медиа сайта: как сжимать картинки при загрузке, какие форматы
 * отдавать сайту, перекодировать ли видео — и сжатие уже загруженного с
 * отчётом «было — стало».
 *
 * Настройки общие для окружений и меняются с правом на устройство сайта;
 * сжать загруженное может каждый, кто пишет в медиатеку.
 */
export function OptimizationDialog({
  siteId,
  env,
  canShape,
  canWrite,
}: {
  siteId: string;
  env: Environment;
  canShape: boolean;
  canWrite: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        tone="neutral"
        iconLeft={<Gauge size={15} />}
        onClick={() => {
          setOpen(true);
        }}
      >
        Оптимизация
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        {open && (
          <DialogContent
            title="Оптимизация медиа"
            description="Картинки сжимаются при загрузке, сайт получает современные форматы, видео грузится кусками — даже если загрузили фото на 8 МБ и ролик с телефона."
            size="md"
          >
            <Body siteId={siteId} env={env} canShape={canShape} canWrite={canWrite} />
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}

function Body({
  siteId,
  env,
  canShape,
  canWrite,
}: {
  siteId: string;
  env: Environment;
  canShape: boolean;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['media-optimization', siteId, env];

  const state = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await mediaOptimization(siteId, env);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос оптимизации');
      }

      return response.data;
    },
  });

  const space = useQuery({
    queryKey: ['media-space', siteId],
    queryFn: async () => {
      const response = await mediaSpace(siteId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос места');
      }

      return response.data;
    },
  });

  const [draft, setDraft] = useState<SettingsBody>();
  const [saving, setSaving] = useState(false);
  const [run, setRun] = useState<{ done: number; total: number; saved: number }>();

  if (state.data === undefined) {
    return <Skeleton height={320} />;
  }

  const data = state.data;
  const settings = draft ?? data.settings;
  const available = data.available ?? {};
  const formats = settings.formats ?? [];
  const changed = draft !== undefined && JSON.stringify(draft) !== JSON.stringify(data.settings);
  const saved = data.before - data.after;

  const set = (patch: Partial<SettingsBody>) => {
    setDraft({ ...settings, ...patch });
  };

  const save = async () => {
    setSaving(true);

    try {
      await mediaSetSettings(siteId, settings);
      toast.success('Настройки сохранены', { description: 'Действуют на новые загрузки.' });
      setDraft(undefined);
      void queryClient.invalidateQueries({ queryKey });
    } catch (cause) {
      toast.error('Не удалось сохранить', {
        description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Порциями: сервер сжимает по 10 и говорит, сколько осталось, — так виден
  // ход, и долгий запрос не упирается в таймаут.
  const optimizeAll = async () => {
    let total = data.pending;
    let done = 0;
    let saved = 0;

    setRun({ done, total, saved });

    try {
      for (;;) {
        const response = await mediaOptimize(siteId, env);

        if (response.status !== 200) {
          throw new Error('Неожиданный ответ на сжатие');
        }

        done += response.data.processed;
        saved += response.data.saved;
        total = Math.max(total, done + response.data.remaining);
        setRun({ done, total, saved });

        if (response.data.remaining === 0 || response.data.processed === 0) {
          break;
        }
      }

      toast.success('Картинки сжаты', { description: `Сэкономлено ${humanSize(saved)}.` });
    } catch (cause) {
      toast.error('Сжатие прервалось', {
        description:
          cause instanceof ApiError
            ? cause.detail
            : 'Попробуйте ещё раз — продолжится с того же места.',
      });
    } finally {
      setRun(undefined);
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['media', siteId] });
    }
  };

  return (
    <div className={styles['body']}>
      <section className={styles['stats']}>
        <div className={styles['stat']}>
          <span className={styles['stat__value']}>{saved > 0 ? humanSize(saved) : '—'}</span>
          <span className={styles['stat__label']}>
            {saved > 0
              ? `сэкономлено: ${humanSize(data.before)} → ${humanSize(data.after)}`
              : 'сэкономлено на сжатии'}
          </span>
        </div>
        <div className={styles['stat']}>
          <span className={styles['stat__value']}>{data.pending}</span>
          <span className={styles['stat__label']}>картинок ещё не сжато</span>
        </div>
        {space.data !== undefined && (
          <div className={`${styles['stat'] ?? ''} ${styles['stat--wide'] ?? ''}`}>
            <span className={styles['stat__value']}>{humanSize(space.data.total)}</span>
            <span className={styles['stat__label']}>
              занято в хранилище —{' '}
              {(space.data.environments ?? [])
                .map(
                  (item) =>
                    `${item.environment === 'production' ? 'Production' : 'Development'} ${humanSize(item.bytes)}`,
                )
                .join(' · ')}
              {(space.data.environments ?? []).some((item) => item.trash_bytes > 0) &&
                ` · в корзине ${humanSize(
                  (space.data.environments ?? []).reduce((sum, item) => sum + item.trash_bytes, 0),
                )}, удалится сама через 30 дней`}
            </span>
          </div>
        )}
      </section>

      {canWrite && data.pending > 0 && (
        <div className={styles['run']}>
          {run === undefined ? (
            <Button
              iconLeft={<Sparkles size={15} />}
              onClick={() => {
                void optimizeAll();
              }}
            >
              Сжать загруженные
            </Button>
          ) : (
            <>
              <progress
                className={styles['progress']}
                value={run.done}
                max={Math.max(run.total, 1)}
              />
              <span className={styles['muted']}>
                {run.done} из {run.total} · сэкономлено {humanSize(run.saved)}
              </span>
            </>
          )}
        </div>
      )}

      <section className={styles['settings']}>
        <h3 className={styles['title']}>Как обрабатывать новые загрузки</h3>

        <Field
          label="Предел стороны картинки"
          hint="Больше — уменьшается при загрузке. 2560 хватает и для экрана Retina во всю ширину."
        >
          <Segmented
            aria-label="Предел стороны картинки"
            value={String(settings.max_side)}
            disabled={!canShape}
            onValueChange={(value) => {
              set({ max_side: Number(value) });
            }}
            options={SIDES.map((side) => ({ value: String(side), label: `${String(side)} px` }))}
          />
        </Field>

        <Field label="Качество" hint="82 на глаз не отличить от оригинала, а файл в разы легче.">
          <Slider
            aria-label="Качество"
            min={60}
            max={95}
            value={settings.quality}
            disabled={!canShape}
            onValueChange={(value) => {
              set({ quality: value });
            }}
          />
        </Field>

        <Field
          label="Форматы для сайта"
          hint="Браузер получит самый лёгкий из тех, что понимает; старый — JPEG или PNG."
        >
          <div className={styles['formats']}>
            {(['avif', 'webp'] as const).map((format) => (
              <Checkbox
                key={format}
                checked={formats.includes(format)}
                disabled={!canShape || available[format] !== true}
                onCheckedChange={(value) => {
                  set({
                    formats:
                      value === true
                        ? [...formats, format]
                        : formats.filter((item) => item !== format),
                  });
                }}
              >
                {format === 'avif' ? 'AVIF — легче всего' : 'WebP'}
                {available[format] !== true && ' (сервер не умеет)'}
              </Checkbox>
            ))}
          </div>
        </Field>

        <Switch
          checked={settings.keep_original}
          disabled={!canShape}
          onCheckedChange={(value) => {
            set({ keep_original: value });
          }}
        >
          Хранить оригиналы — на случай, если понадобится исходник
        </Switch>

        <Switch
          checked={settings.video_hls}
          disabled={!canShape || available['hls'] !== true}
          onCheckedChange={(value) => {
            set({ video_hls: value });
          }}
        >
          Перекодировать видео в HLS — грузится кусками, качество под скорость сети
          {available['hls'] !== true && ' (сервер не умеет)'}
        </Switch>

        {canShape ? (
          <div className={styles['actions']}>
            <Button
              loading={saving}
              disabled={!changed}
              onClick={() => {
                void save();
              }}
            >
              Сохранить
            </Button>
          </div>
        ) : (
          <p className={styles['muted']}>Настройки меняют администратор и разработчик сайта.</p>
        )}
      </section>
    </div>
  );
}
